import { ArrowRight, CheckCircle2, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

interface ServiceHeroProps {
  /** Small eyebrow label shown above the title (uppercase) */
  eyebrow?: string;
  /** Main headline. Wrap the highlighted phrase in <Highlight>. */
  title: ReactNode;
  /** Sub-paragraph under the title */
  description: ReactNode;
  /** Items inside the "incluye" pill strip */
  includes?: string[];
  /** Optional list of small chips with icons (e.g. platform names) */
  chips?: { label: string; Icon?: LucideIcon }[];
  /** Optional content rendered between description and CTAs (e.g. floating platform logos) */
  decoration?: ReactNode;
  /** Optional content rendered to the right (mockup, logos grid, screenshot) */
  rightSlot?: ReactNode;
  /** Primary CTA */
  primaryCta?: { label: string; href?: string; onClick?: () => void };
  /** Secondary CTA */
  secondaryCta?: { label: string; href?: string; onClick?: () => void };
}

/** Inline gradient highlight matching the homepage Hero */
export const Highlight = ({ children }: { children: ReactNode }) => (
  <span className="text-gradient">{children}</span>
);

const ServiceHero = ({
  eyebrow,
  title,
  description,
  includes,
  chips,
  decoration,
  rightSlot,
  primaryCta = { label: "Agendar reunión", href: "/#contact" },
  secondaryCta = { label: "Ver planes", href: "/#pricing" },
}: ServiceHeroProps) => {
  const renderCta = (
    cta: { label: string; href?: string; onClick?: () => void },
    variant: "primary" | "secondary"
  ) => {
    const cls =
      variant === "primary"
        ? "btn-pill bg-foreground text-background hover:bg-foreground/90 px-8 py-4"
        : "btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4";
    const inner = (
      <>
        {cta.label}
        <ArrowRight size={16} />
      </>
    );
    if (cta.onClick) {
      return (
        <button onClick={cta.onClick} className={cls}>
          {inner}
        </button>
      );
    }
    if (cta.href?.startsWith("/")) {
      return (
        <Link to={cta.href} className={cls}>
          {inner}
        </Link>
      );
    }
    return (
      <a href={cta.href} className={cls}>
        {inner}
      </a>
    );
  };

  return (
    <section className="relative pt-4 pb-20">


      <div className="relative z-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* LEFT — Text */}
        <div className={rightSlot ? "lg:col-span-7" : "lg:col-span-12 text-center max-w-4xl mx-auto flex flex-col items-center"}>
          {eyebrow && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs uppercase tracking-widest text-muted-foreground font-body mb-6 animate-fade-in-up">
              {eyebrow}
            </div>
          )}

          <h1 className="font-display text-[2.75rem] sm:text-6xl lg:text-[5rem] uppercase mb-8 leading-[0.95] animate-fade-in-up">
            {title}
          </h1>

          <p
            className={`font-body text-lg md:text-xl text-muted-foreground max-w-xl mb-10 animate-fade-in-up ${!rightSlot ? "mx-auto" : ""}`}
            style={{ animationDelay: "0.1s" }}
          >
            {description}
          </p>

          {decoration && (
            <div className="mb-10 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              {decoration}
            </div>
          )}

          <div
            className={`flex flex-wrap gap-4 animate-fade-in-up ${!rightSlot ? "justify-center" : ""}`}
            style={{ animationDelay: "0.2s" }}
          >
            {renderCta(primaryCta, "primary")}
            {renderCta(secondaryCta, "secondary")}
          </div>

          {chips && chips.length > 0 && (
            <div
              className={`mt-10 flex flex-wrap gap-3 animate-fade-in-up ${!rightSlot ? "justify-center" : ""}`}
              style={{ animationDelay: "0.3s" }}
            >
              {chips.map(({ label, Icon }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground/80 font-body"
                >
                  {Icon && <Icon size={14} className="text-primary" />}
                  {label}
                </span>
              ))}
            </div>
          )}

          {includes && includes.length > 0 && (
            <div
              className="mt-10 inline-flex flex-wrap gap-x-5 gap-y-2 px-6 py-3 rounded-2xl bg-card border border-border animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              {includes.map((item) => (
                <span key={item} className="flex items-center gap-1.5 text-sm text-muted-foreground font-body">
                  <CheckCircle2 size={14} className="text-primary" />
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — optional mockup / decorative slot */}
        {rightSlot && (
          <div className="lg:col-span-5 relative hidden lg:block">{rightSlot}</div>
        )}
      </div>
    </section>
  );
};

export default ServiceHero;
